"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import { io, type Socket } from "socket.io-client";
import toast from "react-hot-toast";

// Notification type definition
interface Notification {
  id: string;
  message: string;
  type: "message";
  createdAt: string;
}

interface NotificationContextType {
  notifications: Notification[];
  fetchNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [socket, setSocket] = useState<Socket | null>(null);

  // Connect to socket
  useEffect(() => {
    const newSocket = io(
      process.env.REACT_APP_SOCKET_URL,
      {
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      }
    );

    setSocket(newSocket);

    // ✅ Return a cleanup function
    return () => {
      newSocket.disconnect();
    };
  }, []);

  // Handle socket events
  useEffect(() => {
    if (!socket) return;

    socket.on("notification", (notification: Notification) => {
      setNotifications((prev) => [notification, ...prev]);

      // Show toast notification from top-left
      toast.custom((t) => (
        <div
          className={`fixed top-0 left-0 z-50 transition-transform duration-300 ${
            t.visible ? "translate-x-0 translate-y-0" : "-translate-x-full"
          }`}
        >
          <div className="mx-auto max-w-md bg-gray-900 text-white rounded-lg shadow-lg">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center space-x-4">
                <div className="bg-purple-600 text-white rounded-full p-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 17h5l-1.405-1.405M15 17l-5.293-5.293a1 1 0 00-1.414 0L4 17m11-11V4m0 0h-2m2 0h2"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold">New Message</p>
                  <p className="text-sm text-gray-300">
                    {notification.message || "Thanks for your message! I’ll get back to you shortly."}
                  </p>
                </div>
              </div>
              <button
                onClick={() => toast.dismiss(t.id)}
                className="text-sm text-gray-400 hover:text-gray-200 focus:outline-none"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ));
    });

    socket.on("connect_error", (err) => {
      console.error("Socket connection error:", err);
    });

    socket.on("connect", () => {
      console.log("Socket connected");
    });

    socket.on("disconnect", () => {
      console.log("Socket disconnected");
    });
  }, [socket]);

  // Optional: Fetch existing notifications from backend
  const fetchNotifications = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/notifications`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch notifications");
      }
      const data = await response.json();
      setNotifications(data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  return (
    <NotificationContext.Provider value={{ notifications, fetchNotifications }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error("useNotifications must be used within a NotificationProvider");
  }
  return context;
};
